import { useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { CircleCheck, KeyRound, Lock } from 'lucide-react';

import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { ApiRequestError } from '@/lib/apiClient';
import { CONFIRMATION_NO_PASTE, PASSWORD_NO_COPY } from '@/lib/forms';
import { PasswordStrength } from '../components/PasswordStrength';
import { authApi } from '../api/auth.api';
import { setPasswordSchema, type SetPasswordValues } from '../schemas/password.schema';
import { useLiveFormErrors } from '@/hooks/useLiveFormErrors';

/**
 * Where both the invite link and the forgot-password link land.
 *
 * An institute's first administrator arrives here from the email we send when the
 * institute is created, and never had a password to begin with; everyone else
 * arrives here having forgotten theirs. Same token, same screen — the only
 * difference is the wording.
 */
export function SetPasswordPage() {
  const navigate = useNavigate();
  const { hash } = useLocation();
  const [searchParams] = useSearchParams();
  // Emailed links carry the secret after `#`, which no server ever receives.
  // `?token=` is still read for links sent before that change — they stay
  // usable until they expire (24 hours at most), after which this fallback can go.
  const token = hash.slice(1) || searchParams.get('token') || '';

  const [formError, setFormError] = useState<string | null>(null);
  const [isDone, setIsDone] = useState(false);

  const form = useForm<SetPasswordValues>({
    resolver: zodResolver(setPasswordSchema),
    defaultValues: { newPassword: '', confirmPassword: '' },
    // Checked as soon as a field is left, then on every keystroke — so what is
    // wrong shows while it is being fixed, not only after pressing save.
    mode: 'onTouched',
  });
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = form;
  useLiveFormErrors(form);

  const newPassword = watch('newPassword');
  const confirmPassword = watch('confirmPassword');

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);
    try {
      await authApi.resetPassword({ token, ...values });
      setIsDone(true);
    } catch (error) {
      setFormError(
        error instanceof ApiRequestError
          ? error.message
          : 'Something went wrong. Please try again.',
      );
    }
  });

  // A link that arrived without its token is broken before anything is typed —
  // say so now rather than after a password has been chosen twice.
  if (!token) {
    return (
      <div className="flex flex-col gap-6">
        <div>
          <p className="lbl mb-2">Set password</p>
          <h1 className="text-2xl">This link is incomplete</h1>
        </div>
        <Alert tone="critical" title="We could not open this link">
          Open it again straight from the original email, or ask for a new link below.
        </Alert>
        <Button fullWidth onClick={() => navigate('/forgot-password')}>
          Send me a new link
        </Button>
      </div>
    );
  }

  if (isDone) {
    return (
      <div className="flex flex-col gap-6">
        <div className="flex flex-col items-center gap-3 text-center">
          <span className="grid h-11 w-11 place-items-center rounded-field bg-surface-2 text-ok">
            <CircleCheck className="h-5 w-5" aria-hidden />
          </span>
          <div>
            <h1 className="text-2xl">Password set</h1>
            <p className="mt-1.5 text-sm text-ink-2">
              Sign in with your new password. Any other device that was signed in has been signed
              out.
            </p>
          </div>
        </div>

        <Button size="lg" fullWidth onClick={() => navigate('/login', { replace: true })}>
          Go to sign in
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div>
        <p className="lbl mb-2">Set password</p>
        <h1 className="text-2xl">Choose your password</h1>
        <p className="mt-1.5 text-sm text-ink-2">
          At least 8 characters, with an uppercase letter, a lowercase letter, a number and a
          special character.
        </p>
      </div>

      {formError && (
        <Alert tone="critical" title="Could not set your password">
          {formError}{' '}
          <Link to="/forgot-password" className="font-[560] underline">
            Request a new link
          </Link>
        </Alert>
      )}

      <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
        <Input
          label="New password"
          autoComplete="new-password"
          autoFocus
          isPassword
          leftIcon={<KeyRound className="h-4 w-4" />}
          error={errors.newPassword?.message}
          {...PASSWORD_NO_COPY}
          {...register('newPassword')}
        />
        <Input
          label="Confirm new password"
          autoComplete="new-password"
          isPassword
          leftIcon={<Lock className="h-4 w-4" />}
          hint="Type it again — pasting is turned off here."
          error={errors.confirmPassword?.message}
          {...CONFIRMATION_NO_PASTE}
          {...register('confirmPassword')}
        />

        {/* The rules tick off as they are met, and "Both entries match" ticks
            once the confirmation agrees — the one mistake nobody can see
            behind the dots. */}
        <PasswordStrength value={newPassword} confirmation={confirmPassword} />

        <Button type="submit" size="lg" fullWidth isLoading={isSubmitting} className="mt-1">
          {isSubmitting ? 'Saving…' : 'Save password'}
        </Button>
      </form>

      <p className="text-center text-xs text-ink-3">
        This link works once and expires. If it has already been used, ask for a new one.
      </p>
    </div>
  );
}
